import type { ThreadComment } from '../../data/client';
import { useDeleteComment } from '../../data/hooks';
import { formatDateTime, formatRelative } from '../../fields/format';
import {
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
  cx,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  Icon,
  IconButton,
} from '../../ui';
import { useSession } from '../auth/useSession';
import { actorParts } from './ActorLabel';

/**
 * A THREADED-FLAT discussion: top-level comments, and one level of replies under each
 * (notes-tasks-comments.md 2.4).
 *
 * The one-level rule is enforced at WRITE time, so this component never has to recurse
 * and never has to decide what a third level would look like. It renders exactly what
 * the shape guarantees.
 *
 * A DELETED comment renders as a TOMBSTONE rather than disappearing. A thread whose
 * middle vanished reads as if people were answering nothing, and the shape of a
 * conversation is part of what it said.
 *
 * WAVE 4 ROUND 2: A COMMENT IS THE STREAM'S ROW, IN EVERY FILTER. Round 1 rebuilt the
 * stream's entries into one list row each and left this list alone, so the SAME comment
 * read as a row under All - mail glyph, the text as the title, the writer as the secondary
 * line, the time trailing - and as the old thread block under Comments: an avatar, a name
 * and a time on one line with the body under it. One thing may not have two constructions
 * on one screen, and the one the spec names is the row (`spec/screens/record.md` section 2
 * item 5), so the thread's own row list is gone and what is left is the stream's row with
 * the two things a comment has and an event has not: an action menu, and an answer relation.
 * kit: Apple macOS 27 UI Kit.sketch / Lists/Light/Rows/Default
 *
 * AND THE ANSWER RELATION IS SAID IN WORDS, because the row is flat. A reply used to be an
 * indented list under its parent; in the stream's construction there is no second column to
 * indent into, so the secondary line says "Reply to Lena Roth" - the same move round 1 made
 * with INV-3, which put the writer's kind into words when the avatar went.
 *
 * IT HAS NO COMPOSER OF ITS OWN AND NO CONTROLS AT REST (wave 4, C2-12). The resizable
 * textarea with a "Comment" push button under it was a web form on a screen built out of kit
 * parts, and the envelope and the ellipsis drawn on every comment at rest were two controls
 * on a row Apple draws with none. Writing a comment is the record's one floating panel
 * (`NoteComposer`), reached from the stream's entry row and from a row's Reply; the actions
 * are in the row's context menu, which is where the secondary click and the touch-and-hold
 * already look, plus one More that appears under the pointer.
 * hig: https://developer.apple.com/design/human-interface-guidelines/context-menus
 */
export interface CommentTarget {
  /** The note whose thread this is, when it is a note's own thread rather than the record's. */
  readonly noteId?: string | undefined;
  readonly replyToCommentId?: string | undefined;
  readonly replyToName?: string | undefined;
}

export interface CommentThreadProps {
  readonly comments: readonly ThreadComment[];
  readonly objectId: string;
  readonly recordId: string;
  /** Opens the record's one writing panel, aimed at this thread. */
  readonly onCompose: (target: CommentTarget) => void;
  /** The note this thread hangs off, passed straight back out through `onCompose`. */
  readonly noteId?: string | undefined;
  readonly className?: string | undefined;
}

export function CommentThread({
  comments,
  objectId,
  recordId,
  onCompose,
  noteId,
  className,
}: CommentThreadProps) {
  const top = comments.filter((comment) => comment.replyToCommentId === null);
  const repliesOf = (id: string): readonly ThreadComment[] =>
    comments.filter((comment) => comment.replyToCommentId === id);

  const reply = (comment: ThreadComment): void =>
    onCompose({
      ...(noteId === undefined ? {} : { noteId }),
      replyToCommentId: comment.id,
      replyToName: comment.createdByName ?? 'this comment',
    });

  if (top.length === 0) return null;

  /**
   * THE THREAD AS ONE FLAT LIST, a comment followed by its answers, which is the order it
   * was read in before the indent carried it. The hairline between rows is the stream's own
   * (`.record-timeline-row + .record-timeline-row`), and it only works between SIBLINGS -
   * a nested list would have put the answers outside that relation as well as outside the
   * construction.
   */
  const rows = top.flatMap((comment) => [
    { comment, answering: null as ThreadComment | null },
    ...repliesOf(comment.id).map((answer) => ({ comment: answer, answering: comment })),
  ]);

  return (
    <ol className={cx('record-timeline', className)}>
      {rows.map(({ comment, answering }) => (
        <CommentRow
          key={comment.id}
          comment={comment}
          objectId={objectId}
          recordId={recordId}
          /** Replying to a reply targets its parent anyway; offer the parent. */
          onReply={() => reply(answering ?? comment)}
          answering={answering === null ? null : (answering.createdByName ?? 'this comment')}
        />
      ))}
    </ol>
  );
}

/**
 * THE THREAD'S OWN WAY IN, as the stream's entry row (`spec/screens/record.md` section 2
 * item 5). It is the same row the stream begins with, because it does the same thing: it
 * opens the writing panel. A note's thread says what it is a comment ON; the record's own
 * discussion says the plain verb.
 */
export function CommentEntryRow({
  label,
  onClick,
}: {
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="record-entry-row">
      <span className="record-timeline-glyph">
        <Icon name="mail" size="0.9rem" />
      </span>
      <span>{label}</span>
    </button>
  );
}

interface CommentRowProps {
  readonly comment: ThreadComment;
  readonly objectId: string;
  readonly recordId: string;
  readonly onReply: () => void;
  /** The name this comment answers, when it is a reply. */
  readonly answering: string | null;
}

function CommentRow({ comment, objectId, recordId, onReply, answering }: CommentRowProps) {
  const session = useSession();
  const deleteComment = useDeleteComment(objectId, recordId);
  const deleted = comment.deletedAt !== null;

  if (deleted) {
    /* A tombstone is a ROW like the rest, because it is what stands in the conversation's
       place: the trash glyph the stream already gives a deleted comment, and the sentence
       in the subtle colour instead of a body. */
    return (
      <li className="record-timeline-row">
        <span className="record-timeline-glyph">
          <Icon name="trash" size="0.9rem" />
        </span>
        <div className="record-timeline-body">
          <span className="record-timeline-title text-ink-subtle">Comment deleted</span>
        </div>
        <span className="record-comment-trailing">
          <time
            dateTime={comment.deletedAt ?? comment.updatedAt}
            title={formatDateTime(comment.deletedAt ?? comment.updatedAt)}
            className="record-timeline-time"
          >
            {formatRelative(comment.deletedAt ?? comment.updatedAt)}
          </time>
        </span>
      </li>
    );
  }

  const mine =
    session.data?.role === 'admin' ||
    (session.data?.id !== undefined && comment.createdBy.userId === session.data.id);
  /**
   * A COMMENT THAT ONLY EXISTS HERE CANNOT BE ANSWERED OR DELETED.
   *
   * Its id was minted by this browser, so a reply would name a parent the server has
   * never seen and a delete would address nothing - a 404 handed to whoever types
   * fastest. Disabling rather than hiding keeps the row's geometry, so nothing jumps
   * when the server's row lands a moment later.
   */
  const pending = comment.pending === true;
  const replyLabel = `Reply to ${comment.createdByName ?? 'this comment'}`;
  const remove = () => deleteComment.mutate(comment.id);
  /**
   * THE WRITER AS THE STREAM SAYS IT: the resolved name where there is one, and for an
   * automated writer its kind and the context it acted in as their own words. `actorParts`
   * is the one place that decides this, so a comment and an event can never describe the
   * same writer differently.
   */
  const writer = actorParts({
    ...comment.createdBy,
    ...(comment.createdByName === null ? {} : { displayName: comment.createdByName }),
  });
  const adopted = comment.createdBy.source !== 'MANUAL' && comment.humanTouchedAt !== null;
  const edited = comment.updatedAt !== comment.createdAt;

  const row = (
    <li
      className={cx('record-timeline-row', pending && 'opacity-70')}
      aria-busy={pending || undefined}
    >
      <span className="record-timeline-glyph">
        <Icon name="mail" size="0.9rem" />
      </span>
      <div className="record-timeline-body">
        <span className="record-timeline-title">{comment.body}</span>
        <p className="record-timeline-secondary">
          <span>{writer.name}</span>
          {/* INV-3: an agent write must never be mistakable for a human one, and in a row
              with no avatar the words are what carry that. */}
          {writer.kind !== null && <span className="record-timeline-kind">{writer.kind}</span>}
          {writer.context !== null && <span className="truncate">{writer.context}</span>}
          {answering !== null && <span className="truncate">{`Reply to ${answering}`}</span>}
          {adopted && <span>{`edited by ${comment.updatedByName ?? 'a person'}`}</span>}
          {edited && !adopted && <span>edited</span>}
        </p>
      </div>
      {/*
        THE TRAILING COLUMN CARRIES BOTH, and the More keeps its width whether it is drawn
        or not: a control that appears under the pointer must not move the time it sits
        beside, which is what an absolutely positioned one over the time would have done.
        A thumb has no pointer, which is why the context menu carries the same two items and
        opens on touch and hold; a keyboard reaches this through `:focus-within`, which is
        the rule `.record-comment-more` states in the stylesheet.
      */}
      <span className="record-comment-trailing">
        {!pending && (
          <span className="record-comment-more">
            <DropdownMenu
              trigger={
                <IconButton
                  icon="more-horizontal"
                  label={`Actions for ${comment.createdByName ?? 'this comment'}`}
                  size="sm"
                />
              }
              align="end"
            >
              <DropdownItem icon="mail" onSelect={onReply}>
                Reply
              </DropdownItem>
              {mine && (
                <>
                  <DropdownSeparator />
                  <DropdownItem icon="trash" tone="danger" onSelect={remove}>
                    Delete
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </span>
        )}
        <time
          dateTime={comment.createdAt}
          title={formatDateTime(comment.createdAt)}
          className="record-timeline-time"
        >
          {formatRelative(comment.createdAt)}
        </time>
      </span>
    </li>
  );

  if (pending) return row;

  return (
    <ContextMenu trigger={row}>
      <ContextMenuItem icon="mail" onSelect={onReply}>
        {/* "Reply to <name>" rather than "Reply": the menu names what it will answer, and
            the panel that opens says the same name back. */}
        {replyLabel}
      </ContextMenuItem>
      {mine && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem icon="trash" tone="danger" onSelect={remove}>
            Delete
          </ContextMenuItem>
        </>
      )}
    </ContextMenu>
  );
}
